import { Query } from '@apollo/client/react/components';
import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { AbsoluteSpinner } from '@/se/components/Spinner';
import LineChart from './LineChart';
import get from 'lodash/get';
import {
  ConfigButton,
  currentHumidityColumn,
  currentTempColumn,
  humidityMinMaxColumn,
  tempMinMaxColumn,
} from './SensorList';
import { TruncatedText } from '@/se/components/typography';
import { sensorsDistribution } from '../../../graph/sensors';
import { withTheme } from '@/se/theme';
import { Paper, Select, MenuItem, FormControl, InputLabel, Checkbox, ListItemText } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import responsive from '@/se/utilities/responsive';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  > * {
    margin: 1em 0;
  }

  > :first-child {
    margin: 0;
    margin-top: 1.5em;
  }
  min-height: 20em;
`;

const colors = ['#E34141', '#00A7F7'];

const Row = styled.div`
  display: flex;
  flex: 1 0 auto;
  justify-content: flex-end;
  font-size: 0.7em;
  opacity: 0.8;

  span {
    font-size: 1.1em;
  }

  > span {
    margin-left: 1.2em;
  }

  ${responsive.md.andSmaller`
    flex-direction: column;

    > span {
      margin: .5em 0;
    }
  `};
`;

const Empty = styled.label`
  justify-content: center;
  display: flex;
  flex: 1 0 auto;
  margin: auto;
  align-items: center;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;

  ${responsive.md.andSmaller`
    flex-direction: column;
  `};
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;

  line-height: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.44px;
  font-weight: 500;

  h4 {
    font-size: 1em;
    margin-bottom: 0.2em;
  }

  h5 {
    font-size: 0.75em;
    color: ${withTheme(theme => theme.textColor.alpha(0.5).string())};
  }
`;

const Action = styled.div`
  ${responsive.md.andSmaller`
    position: absolute;
    top: 1.5em;
    right: 2em;
  `};
`;

const minForStat = (sensor, id) =>
  id.indexOf('temperature') > 0 ? get(sensor, 'temperatureMin.value') : sensor.humidityMin;
const maxForStat = (sensor, id) =>
  id.indexOf('temperature') > 0 ? get(sensor, 'temperatureMax.value') : sensor.humidityMax;

// Check if a distribution has valid data (non-empty array with at least one meaningful non-zero value)
// For humidity, all-zero values indicate sensor doesn't have humidity probe
const hasValidDistributionData = distribution => {
  if (!distribution || !Array.isArray(distribution) || distribution.length === 0) {
    return false;
  }
  // Check if at least one data point has a non-null, non-zero value
  // All zeros typically means no real data (null coerced to 0)
  return distribution.some(point => point && point.value !== null && point.value !== undefined && point.value !== 0);
};

// Check if sensor has valid humidity data
// Don't trust currentHumidity alone - it could be garbage from broken parser
// Require EVIDENCE: either user-configured limits OR historical data in distribution
const sensorHasHumidityData = (sensor, humidityStat) => {
  // If sensor has humidity min/max configured by user, they expect humidity monitoring
  if (sensor.humidityMin !== null || sensor.humidityMax !== null) {
    return true;
  }
  // Check if distribution has any non-zero values (historical proof of real humidity data)
  if (humidityStat && hasValidDistributionData(humidityStat.distribution)) {
    return true;
  }
  // Don't trust currentHumidity alone - could be garbage from broken MST01V2 parser
  return false;
};

// Check if this is a humidity stat (by id pattern)
const isHumidityStat = id => id && id.toLowerCase().includes('humidity');

const SensorStats = ({ hasHumidity, ...props }) => (
  <TitleContainer>
    <Title>
      <h4>{props.location}</h4>
      <h5>{props.id}</h5>
    </Title>
    <Row>
      <span>
        <TruncatedText>Current Temp</TruncatedText>
        <currentTempColumn.Component data={currentTempColumn.lens(props)} />
      </span>
      <span>
        <TruncatedText>T Min / Max</TruncatedText>
        <tempMinMaxColumn.Component data={tempMinMaxColumn.lens(props)} />
      </span>
      {hasHumidity && (
        <>
          <span>
            <TruncatedText>Current Humidity</TruncatedText>
            <currentHumidityColumn.Component data={currentHumidityColumn.lens(props)} />
          </span>
          <span>
            <TruncatedText>H Min / Max</TruncatedText>
            <humidityMinMaxColumn.Component data={humidityMinMaxColumn.lens(props)} />
          </span>
        </>
      )}
      <Action style={{ marginLeft: '2em', alignSelf: 'top' }}>
        <ConfigButton data={props} />
      </Action>
    </Row>
  </TitleContainer>
);

const Sensor = ({ sensor, stats }) => {
  // Check if sensor has valid humidity data
  const humidityStat = stats.find(stat => isHumidityStat(stat.id));
  const hasHumidity = sensorHasHumidityData(sensor, humidityStat);

  // Debug logging for humidity detection
  if (sensor.id === 'C30000301769') {
    console.log('[SensorGrid] Deep Freezer humidity check:', {
      sensorId: sensor.id,
      currentHumidity: get(sensor, 'measurement.humidity'),
      humidityMin: sensor.humidityMin,
      humidityMax: sensor.humidityMax,
      hasHumidityStat: !!humidityStat,
      distributionHasData: humidityStat ? hasValidDistributionData(humidityStat.distribution) : false,
      hasHumidity,
    });
  }

  // Filter out humidity charts if sensor doesn't have humidity data
  // This handles sensors without humidity probes (like MST01V2 deep freezer sensors)
  const validStats = stats.filter(stat => {
    // Always show temperature charts
    if (!isHumidityStat(stat.id)) {
      return true;
    }
    // Only show humidity chart if it has valid data
    return hasHumidity;
  });

  return (
    <Box
      component={Paper}
      p={2}
      style={{
        flex: '0 1 100%',
        position: 'relative',
        opacity: sensor.enabled ? 1 : 0.4,
      }}
    >
      <SensorStats {...sensor} hasHumidity={hasHumidity} />
      {validStats.length > 0 ? (
        validStats.map(({ id, distribution, unit, title }, i) => (
          <LineChart
            data={distribution}
            unit={unit}
            key={id}
            color={colors[i % colors.length]}
            label={title}
            min={minForStat(sensor, id)}
            max={maxForStat(sensor, id)}
          />
        ))
      ) : (
        <Empty>No data for selected period</Empty>
      )}
    </Box>
  );
};

const getDistributions = (id, data) => get(data, 'sensorsDistribution', []).filter(_ => _.id.startsWith(id));

// Check if sensor has data in the current period
const sensorHasData = (sensorId, distributionData) => {
  const distributions = getDistributions(sensorId, distributionData);
  return distributions.some(d => d.distribution && d.distribution.length > 0);
};

const Sensors = ({ sensors, loading, sensorsDistribution, selectedSensorIds }) =>
  !loading ? (
    (() => {
      // Only show sensors that have data in the selected timeframe
      const sensorsWithData = sensors.filter(s => sensorHasData(s.id, sensorsDistribution));

      // If specific sensors are selected, filter to only those
      const displaySensors =
        selectedSensorIds && selectedSensorIds.length > 0
          ? sensorsWithData.filter(s => selectedSensorIds.includes(s.id))
          : sensorsWithData;

      return displaySensors.length > 0 ? (
        displaySensors.map(sensor => (
          <Sensor sensor={sensor} key={sensor.id} stats={getDistributions(sensor.id, sensorsDistribution)} />
        ))
      ) : (
        <Empty style={{ flex: 1, padding: '2em' }}>
          {sensorsWithData.length > 0 ? 'No sensors match your filter selection' : 'No sensor data for selected period'}
        </Empty>
      );
    })()
  ) : (
    <AbsoluteSpinner />
  );

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  * + * {
    margin-left: 0.5em;
  }
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const StyledFormControl = styled(FormControl)`
  width: 200px;
`;

const STORAGE_KEY = 'sensor-chart-filter';

const SensorGrid = ({ filter, sensors, loading: sensorsLoading, sensorIds }) => {
  // Load saved selection from localStorage (stores array of sensor IDs)
  const [selectedSensorIds, setSelectedSensorIds] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save selection to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedSensorIds));
    } catch {
      // Ignore errors (e.g., private browsing mode)
    }
  }, [selectedSensorIds]);

  const handleSelectionChange = useCallback(event => {
    const value = event.target.value;
    // Handle "all" selection - when empty array is selected, show all
    setSelectedSensorIds(typeof value === 'string' ? value.split(',') : value);
  }, []);

  // Filter to only enabled sensors for Chart View
  // Disabled sensors shouldn't write measurements, so exclude them from charts
  // Use strict equality to exclude sensors with enabled=false, null, or undefined
  const enabledSensors = sensors.filter(s => s.enabled === true);
  const enabledSensorIds = enabledSensors.map(s => s.id);

  return (
    <Container>
      <Query
        query={sensorsDistribution}
        variables={{ dateRange: filter.dateRange.toJSON(), sensors: enabledSensorIds }}
        pollInterval={60000}
        fetchPolicy={'network-only'}
      >
        {({ data, loading: queryLoading }) => {
          // Show loading state while query is loading or sensors subscription is loading
          const isLoading = sensorsLoading || queryLoading;

          // Build options from enabled sensors that have data
          const sensorsWithData = enabledSensors.filter(s => sensorHasData(s.id, data));

          // Clean up selected IDs - remove any that no longer have data
          const validSelectedIds = selectedSensorIds.filter(id => sensorsWithData.some(s => s.id === id));

          // If selection changed due to cleanup, update state
          if (validSelectedIds.length !== selectedSensorIds.length) {
            // Schedule state update for next tick to avoid render-during-render
            setTimeout(() => setSelectedSensorIds(validSelectedIds), 0);
          }

          return (
            <>
              {sensorsWithData.length > 1 && (
                <FilterBar>
                  <StyledFormControl variant="outlined" size="small">
                    <InputLabel id="sensor-filter-label">Filter sensors</InputLabel>
                    <Select
                      labelId="sensor-filter-label"
                      id="sensor-filter"
                      multiple
                      value={validSelectedIds}
                      onChange={handleSelectionChange}
                      label="Filter sensors"
                      renderValue={selected =>
                        selected.length === 0
                          ? `All (${sensorsWithData.length})`
                          : `${selected.length} of ${sensorsWithData.length} selected`
                      }
                    >
                      {sensorsWithData.map(sensor => (
                        <MenuItem key={sensor.id} value={sensor.id}>
                          <Checkbox checked={validSelectedIds.includes(sensor.id)} />
                          <ListItemText primary={sensor.location || sensor.id} />
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </FilterBar>
              )}
              <Sensors
                sensorsDistribution={data}
                sensors={enabledSensors}
                loading={isLoading}
                selectedSensorIds={validSelectedIds}
              />
            </>
          );
        }}
      </Query>
    </Container>
  );
};

export default SensorGrid;
